import React, { useState } from 'react';
import { Home, ExternalLink, Wrench, Lightbulb, Users, Headphones, Calendar, GraduationCap, Truck, Sparkles, Package, Trophy } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { shouldRedirectToHome, navigateToSectionHome } from '../utils/navigationUtils';
import { useToast } from '../contexts/ToastContext';

interface SidebarProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
  currentSubSection?: string;
  onSubSectionChange?: (subSection: string) => void;
  onOpenAuth?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ 
  activeSection, 
  onSectionChange,
  currentSubSection,
  onSubSectionChange,
  onOpenAuth
}) => {
  const { user, profile } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const { showToast } = useToast();

  const menuItems = [
    { id: 'competicao', label: 'Competição', icon: Trophy },
    { id: 'home', label: 'Home', icon: Home },
    { id: 'mentorias', label: 'Mentorias', icon: GraduationCap },
    { id: 'useful-links', label: 'Ferramentas & Links', icon: ExternalLink },
    { id: 'suppliers', label: 'Fornecedores', icon: Truck },
    { id: 'calendar', label: 'Calendário', icon: Calendar },
    { id: 'community', label: 'Comunidade', icon: Users, requiresAuth: true, requiresActive: true },
    { id: 'support', label: 'Suporte', icon: Headphones },
  ];

  const handleSectionClick = (sectionId: string, requiresAuth?: boolean, requiresActive?: boolean) => {
    // Se a seção requer autenticação e o usuário não está logado, abrir login e mostrar toast
    if (requiresAuth && !user) {
      if (onOpenAuth) {
        onOpenAuth();
      }
      const screenName = sectionId === 'suppliers' ? 'fornecedores' : sectionId === 'community' ? 'comunidade' : 'mentorias';
      showToast({
        type: 'warning',
        title: 'Acesso Restrito',
        message: `Para ter acesso à tela de ${screenName} é necessário estar logado na plataforma.`,
        duration: 5000
      });
      return;
    }

    // Se a seção requer usuário ativo e o usuário está suspenso, não faz nada
    if (requiresActive && profile && !profile.is_active) {
      return;
    }

    // Verificar se deve redirecionar para a página inicial da seção
    if (shouldRedirectToHome(activeSection, sectionId, currentSubSection)) {
      navigateToSectionHome(sectionId, onSectionChange, onSubSectionChange);
    } else {
      onSectionChange(sectionId);
    }
  };

  const isUserSuspended = profile && !profile.is_active;

  return (
    <div 
      className={`fixed left-2 xl:left-4 top-1/2 transform -translate-y-1/2 z-40 bg-[#0f1114] border border-[#1f2329] rounded-xl shadow-2xl transition-all duration-500 ease-in-out hidden md:block ${
        isExpanded ? 'w-56 xl:w-64' : 'w-14 xl:w-16'
      }`}
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
    >
      {/* Navigation Menu */}
      <nav className="py-4 xl:py-6">
        <ul className="space-y-2 xl:space-y-3">
          {menuItems.map(({ id, label, icon: Icon, requiresAuth, requiresActive }) => {
            const isActive = activeSection === id;
            const isCompetition = id === 'competicao';

            return (
              <li key={id} className="px-2">
                <button
                  onClick={() => handleSectionClick(id, requiresAuth, requiresActive)}
                  className={`w-full flex items-center rounded-lg transition-all duration-300 ease-in-out text-left group relative ${
                    isExpanded ? 'space-x-3 xl:space-x-4 px-2 xl:px-3 py-3 xl:py-4' : 'justify-center py-3 xl:py-4'
                  } ${
                    isActive
                      ? 'bg-[#0FFF55] text-[#0f1114] shadow-[0_0_18px_rgba(15,255,85,0.7)] font-bold'
                      : isCompetition
                      ? 'bg-[#0FFF55]/10 border border-[#0FFF55]/40 text-[#0FFF55] hover:bg-[#0FFF55]/20 hover:border-[#0FFF55]/70 shadow-[0_0_14px_rgba(15,255,85,0.3)]'
                      : 'text-white hover:bg-[#1a1d23] hover:text-white'
                  } ${
                    requiresAuth && !user ? 'opacity-50' : ''
                  }`}
                >
                  <div className={`w-5 h-5 xl:w-6 xl:h-6 flex items-center justify-center flex-shrink-0 transition-colors duration-300 ease-in-out relative ${
                    isActive
                      ? 'text-[#0f1114]'
                      : isCompetition
                      ? 'text-[#0FFF55] drop-shadow-[0_0_8px_rgba(15,255,85,0.9)]'
                      : 'text-gray-400 group-hover:text-gray-300'
                  }`}>
                    <Icon 
                      size={isExpanded ? 18 : 20} 
                      className={`xl:w-5 xl:h-5 ${isCompetition && !isActive ? 'animate-pulse' : ''}`} 
                    />
                  </div>
                  
                  {/* Label - Only visible when expanded */}
                  {isExpanded && (
                    <span className={`text-xs xl:text-sm whitespace-nowrap transition-all duration-300 ease-in-out delay-75 flex items-center justify-between flex-1 ${
                      isCompetition && !isActive ? 'font-bold text-[#0FFF55]' : 'font-medium'
                    }`}>
                      <span>{label}</span>
                      {isCompetition && (
                        <span className="text-[9px] bg-[#0FFF55]/20 text-[#0FFF55] px-1.5 py-0.5 rounded font-black tracking-wider uppercase border border-[#0FFF55]/40 ml-1.5 shadow-[0_0_8px_rgba(15,255,85,0.3)]">
                          LIVE
                        </span>
                      )}
                      {requiresAuth && !user && (
                        <span className="block text-[10px] text-gray-500 mt-0.5">Login necessário</span>
                      )}
                      {requiresActive && isUserSuspended && (
                        <span className="block text-[10px] text-gray-500 mt-0.5">Usuário suspenso</span>
                      )}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
};

export default Sidebar;